<template>
  <el-dialog :model-value="modelValue" title="Chorakni nusxalash" width="560px" destroy-on-close
    @update:model-value="onVisibilityChange">
    <div v-loading="copying" class="sbs-copy-form">
      <p class="sbs-copy-intro">
        Bir chorakdagi hujayralar boshqa chorakka ko'chiriladi. Ustunlar va ko'rsatkichlar
        o'zgarmaydi — faqat qiymatlar.
      </p>

      <div class="sbs-period-block">
        <div class="sbs-period-block__title">Manba — {{ currentYear }}-yil</div>
        <el-select v-model="fromQuarter" class="sbs-w-full" @change="checkTarget">
          <el-option v-for="q in sourceQuarters" :key="q.value" :label="q.label" :value="q.value">
            <span class="sbs-q-option">
              <span class="sbs-q-dot" :class="{ 'is-on': hasData(currentYear, q.value) }"
                :style="hasData(currentYear, q.value) ? { background: q.color } : null" />
              {{ q.label }}
            </span>
          </el-option>
        </el-select>
        <div v-if="!hasData(currentYear, fromQuarter)" class="sbs-hint-row is-warn">
          Bu chorakda ma'lumot yo'q — nusxalash xato qaytaradi.
        </div>
      </div>

      <div class="sbs-arrow">↓</div>

      <div class="sbs-period-block">
        <div class="sbs-period-block__title">Maqsad</div>
        <div class="sbs-period-row">
          <el-input-number v-model="toYear" :min="1900" :max="2200" controls-position="right"
            class="sbs-year-input" @change="checkTarget" />
          <el-select v-model="toQuarter" class="sbs-quarter-select" @change="checkTarget">
            <el-option v-for="q in QUARTER_ONLY_OPTIONS" :key="q.value" :label="q.label" :value="q.value">
              <span class="sbs-q-option">
                <span class="sbs-q-dot" :class="{ 'is-on': hasData(toYear, q.value) }"
                  :style="hasData(toYear, q.value) ? { background: q.color } : null" />
                {{ q.label }}
              </span>
            </el-option>
          </el-select>
        </div>
      </div>

      <div class="sbs-field">
        <el-checkbox v-model="withValues">
          Qiymatlar bilan
          <span class="sbs-hint">— o'chirilsa faqat bo'sh "skelet" ko'chadi</span>
        </el-checkbox>
      </div>

      <div v-if="samePeriod" class="sbs-warning is-error">
        Manba va maqsad chorak bir xil bo'lishi mumkin emas.
      </div>
      <template v-else-if="checkingTarget">
        <div class="sbs-hint-row">Maqsad davr tekshirilmoqda...</div>
      </template>
      <template v-else>
        <!-- Ustunlar yilga biriktirilgani uchun boshqa yilda ular boshqa `id` bilan
             yashaydi — copy-period esa hujayrani `column_id` bo'yicha ko'chiradi. -->
        <div v-if="incompatible" class="sbs-warning is-error">
          {{ toYear }}-yilda bu ustunlarning birortasi ham yo'q — nusxalash hech narsa ko'chirmaydi.
          Avval "Yangi yil" oynasi orqali ustunlar skeletini shu yilga ko'chiring.
        </div>
        <div v-else-if="partialColumns" class="sbs-warning">
          ⚠ {{ sourceColumnIds.length }} ta ustundan {{ matchedColumns }} tasi {{ toYear }}-yilda
          mavjud — qolganlarining qiymatlari ko'chmaydi.
        </div>
        <div v-if="targetCellCount > 0" class="sbs-warning">
          ⚠ {{ periodLabel(toYear, toQuarter) }} da allaqachon {{ targetCellCount }} ta ma'lumot bor —
          ular ustiga yoziladi.
        </div>
      </template>
    </div>

    <template #footer>
      <el-button @click="onVisibilityChange(false)">Bekor qilish</el-button>
      <el-button type="primary" :loading="copying" :disabled="samePeriod || incompatible" @click="onCopy">
        Nusxalash
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { ElMessage, ElNotification } from "element-plus";
import {
  API_BASE, QUARTER_ONLY_OPTIONS, quarterOptionsForYear, normalizeQuarter, periodLabel, periodHasData,
} from "./subsidiesTypes";

export default {
  name: "SubsidiesCopyPeriodDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    periods: { type: Array, default: () => [] },
    currentYear: { type: Number, required: true },
    currentQuarter: { type: Number, default: 1 },
    // Joriy yil jadvalidagi ustun id'lari — maqsad yil bilan moslikni tekshirish uchun.
    sourceColumnIds: { type: Array, default: () => [] },
  },
  emits: ["update:modelValue", "copied"],
  data() {
    return {
      QUARTER_ONLY_OPTIONS,
      fromQuarter: 1,
      toYear: null,
      toQuarter: 2,
      withValues: true,
      copying: false,
      checkingTarget: false,
      targetCellCount: 0,
      matchedColumns: 0,
    };
  },
  computed: {
    sourceQuarters() {
      return quarterOptionsForYear(this.periods, this.currentYear);
    },
    samePeriod() {
      return this.toYear === this.currentYear && this.toQuarter === this.fromQuarter;
    },
    incompatible() {
      return this.sourceColumnIds.length > 0 && this.matchedColumns === 0;
    },
    partialColumns() {
      return this.matchedColumns > 0 && this.matchedColumns < this.sourceColumnIds.length;
    },
  },
  watch: {
    modelValue(val) {
      if (!val) return;
      this.fromQuarter = normalizeQuarter(this.currentQuarter);
      this.toYear = this.currentYear;
      // Yillikdan yoki IV chorakdan keyin mantiqiy "keyingi" chorak yo'q — I chorakka qo'yamiz.
      this.toQuarter = this.fromQuarter >= 1 && this.fromQuarter <= 3 ? this.fromQuarter + 1 : 1;
      this.withValues = true;
      this.targetCellCount = 0;
      this.matchedColumns = this.sourceColumnIds.length;
      this.checkTarget();
    },
  },
  methods: {
    periodLabel,
    hasData(year, quarter) {
      return periodHasData(this.periods, year, quarter);
    },
    onVisibilityChange(val) {
      this.$emit("update:modelValue", val);
    },
    // Bitta so'rov ikkita savolga javob beradi: maqsad davrda qancha ma'lumot bor
    // (ustiga yoziladi) va u yerda manba ustunlari mavjudmi.
    checkTarget() {
      if (!this.toYear || this.samePeriod) {
        this.targetCellCount = 0;
        this.matchedColumns = this.sourceColumnIds.length;
        return;
      }
      if (this.toYear === this.currentYear) {
        this.matchedColumns = this.sourceColumnIds.length;
      }
      this.checkingTarget = true;
      this.$http
        .get(`${API_BASE}/admin/matrix`, { params: { year: this.toYear, quarter: this.toQuarter } })
        .then(({ data }) => {
          const targetIds = new Set((data?.columns || []).map((c) => c.id));
          this.matchedColumns = this.sourceColumnIds.filter((id) => targetIds.has(id)).length;
          let count = 0;
          const walk = (rows) => rows.forEach((r) => {
            count += (r.cells || []).filter((c) => c.num !== null || c.text !== null).length;
            if (r.children?.length) walk(r.children);
          });
          walk(data?.rows || []);
          this.targetCellCount = count;
        })
        .catch(() => {
          this.matchedColumns = this.sourceColumnIds.length;
        })
        .finally(() => {
          this.checkingTarget = false;
        });
    },
    onCopy() {
      if (this.samePeriod || this.incompatible) return;
      this.copying = true;
      this.$http
        .post(`${API_BASE}/copy-period`, {
          from_year: this.currentYear,
          from_quarter: this.fromQuarter,
          to_year: this.toYear,
          to_quarter: this.toQuarter,
          with_values: this.withValues,
        })
        .then(({ data }) => {
          const affected = data?.affected ?? 0;
          const skipped = data?.skipped ?? 0;
          // CHANGELOG v2.1 — `skipped`: hujayraning ustuni maqsad yilda mavjud emas.
          if (skipped > 0) {
            ElNotification({
              title: "Bir qism ko'chmadi",
              message: `${affected} ta hujayra ko'chdi, ${skipped} tasi ko'chmadi — ularning ustunlari ` +
                `${this.toYear}-yilga biriktirilmagan.`,
              type: "warning",
              duration: 8000,
            });
          } else {
            ElMessage({ type: "success", message: `${affected} ta hujayra ko'chirildi` });
          }
          this.$emit("copied", { year: this.toYear, quarter: this.toQuarter });
          this.$emit("update:modelValue", false);
        })
        .catch((error) => {
          ElNotification({
            title: "Nusxalashda xatolik",
            message: error?.response?.data?.message || error?.message || "Iltimos, qayta urinib ko'ring.",
            type: "error",
            duration: 8000,
          });
        })
        .finally(() => {
          this.copying = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
$muted: #8a94a6;

.sbs-copy-intro {
  margin: 0 0 16px;
  font-size: 12.5px;
  color: $muted;
}

.sbs-period-block {
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  border-radius: 10px;
  background: #fbfcfe;

  &__title {
    margin-bottom: 8px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: $muted;
  }
}

.sbs-period-row {
  display: flex;
  gap: 10px;
}

.sbs-w-full {
  width: 100%;
}

.sbs-year-input {
  width: 140px;
}

.sbs-quarter-select {
  flex: 1;
}

.sbs-arrow {
  margin: 6px 0;
  text-align: center;
  font-size: 18px;
  color: #007791;
}

.sbs-q-option {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.sbs-q-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #dfe3ea;

  &.is-on {
    box-shadow: 0 0 0 2px rgba(0, 119, 145, 0.12);
  }
}

.sbs-field {
  margin: 16px 0;
}

.sbs-hint {
  font-size: 12px;
  color: $muted;
}

.sbs-hint-row {
  margin-top: 8px;
  font-size: 12px;
  color: $muted;

  &.is-warn {
    color: #92620a;
  }
}

.sbs-warning {
  margin-top: 10px;
  padding: 8px 12px;
  background: rgba(234, 179, 8, 0.12);
  border: 1px solid rgba(234, 179, 8, 0.35);
  border-radius: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #92620a;

  &.is-error {
    background: rgba(225, 29, 72, 0.08);
    border-color: rgba(225, 29, 72, 0.3);
    color: #be123c;
  }
}
</style>
